import { useCallback } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { GiftIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';

import { useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { getAssetSrc, customImageLoader } from '@restworld/utils-common';

import { useV2Router } from './hooks/use-v2-router';

/* --- Sub-components --- */

interface CardProps {
  onClick: () => void;
}

function WithCreditsCard({ onClick, availableCredits }: CardProps & { availableCredits: number }) {
  const { t } = useTranslation('dashboard-page');

  return (
    <WithCreditsCardContainer onClick={onClick}>
      <BackgroundImageWrapper>
        <Image
          src={getAssetSrc('/images/rw-logo-bg.png')}
          alt=""
          fill
          loader={customImageLoader}
          sizes="(max-width: 768px) 100vw, 640px"
          tw="object-cover object-top"
        />
      </BackgroundImageWrapper>
      <CardContent gap={24}>
        <HighlightedBadge>{t('referral_badge_label')}</HighlightedBadge>
        <TextBlock>
          <WithCreditsText>{t('referral_credits_available', { count: availableCredits })}</WithCreditsText>
        </TextBlock>
      </CardContent>
      <DarkButtonWrapper>
        <FrostedCardButton>
          <GiftIcon width={20} height={20} color="#FFFFFF" />
          <PaddedButtonLabel>{t('referral_get_more_credits')}</PaddedButtonLabel>
        </FrostedCardButton>
      </DarkButtonWrapper>
    </WithCreditsCardContainer>
  );
}

function NoCreditsCard({ onClick }: CardProps) {
  const { t } = useTranslation('dashboard-page');

  return (
    <NoCreditsCardContainer onClick={onClick}>
      <BackgroundImageWrapper>
        <Image
          src={getAssetSrc('/images/rw-logo-bg.png')}
          alt=""
          fill
          loader={customImageLoader}
          sizes="(max-width: 768px) 100vw, 640px"
          tw="object-cover object-top"
        />
      </BackgroundImageWrapper>
      <CardContent gap={20}>
        <RestworldBadge>{t('referral_badge_label')}</RestworldBadge>
        <TextBlock>
          <NoCreditsText>{t('referral_no_credits_line1')}</NoCreditsText>
          <NoCreditsText>{t('referral_no_credits_line2')}</NoCreditsText>
        </TextBlock>
      </CardContent>
      <GlowButtonWrapper>
        <FrostedCardButton>
          <GiftIcon width={20} height={20} color="#FFFFFF" />
          <PaddedButtonLabel>{t('referral_get_free_credits')}</PaddedButtonLabel>
        </FrostedCardButton>
      </GlowButtonWrapper>
    </NoCreditsCardContainer>
  );
}

function CardSkeleton() {
  return (
    <SkeletonContainer>
      <SkeletonBadge />
      <SkeletonLine width="75%" />
      <SkeletonLine width="50%" />
      <SkeletonButton />
    </SkeletonContainer>
  );
}

/* --- Main component --- */

function DashboardReferralCard() {
  const { user } = useAuth();
  const router = useV2Router();
  const employersService = useEmployersService();

  const { data, isLoading } = useQuery({
    queryKey: ['referral-link'],
    queryFn: async () => {
      const response = await employersService.getReferralLink();
      return response.data;
    },
    enabled: !!user?.using_ppu_business_model,
  });

  const handleClick = useCallback(() => {
    router.push('/employer/referral');
  }, [router]);

  if (!user?.using_ppu_business_model) {
    return null;
  }

  if (isLoading) {
    return <CardSkeleton />;
  }

  const availableCredits = data?.credits?.available_credits ?? 0;

  if (availableCredits > 0) {
    return <WithCreditsCard onClick={handleClick} availableCredits={availableCredits} />;
  }

  return <NoCreditsCard onClick={handleClick} />;
}

export default DashboardReferralCard;

/* --- Shared styles --- */

const BaseCardContainer = styled.div`
  ${tw`relative flex flex-col justify-center gap-10 rounded-2xl overflow-hidden cursor-pointer`}
  padding: 40px 20px 20px;
`;

const BackgroundImageWrapper = styled.div`
  ${tw`absolute top-0 left-0 w-full h-full pointer-events-none opacity-50`}
  border-radius: 16px;
`;

const CardContent = styled.div<{ gap: number }>`
  ${tw`relative flex flex-col w-full`}
  gap: ${({ gap }) => gap}px;
`;

const TextBlock = styled.div`
  ${tw`flex flex-col gap-2 w-full`}
`;

const BaseBadge = styled.span`
  ${tw`inline-flex items-center justify-center self-start px-2 font-medium text-sm`}
  height: 24px;
  border-radius: 4px;
  line-height: 1.21;
`;

const ButtonWrapper = styled.div`
  ${tw`relative flex w-full`}
  border-radius: 8px;
`;

const CardButton = styled.button`
  ${tw`flex items-center justify-center gap-2 w-full`}
  padding: 8px 16px;
  height: 48px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
`;

const ButtonLabel = styled.span`
  ${tw`font-semibold`}
  font-size: 16px;
  color: #fafafa;
  line-height: 1.21;
`;

/* --- With Credits variant (light background) --- */

const WithCreditsCardContainer = styled(BaseCardContainer)`
  background: #f0ebf8;
  border: 1px solid #dedee1;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 26, 0.05);
`;

const HighlightedBadge = styled(BaseBadge)`
  background: #6633bb;
  color: #fafafa;
`;

const WithCreditsText = styled.span`
  ${tw`w-full font-praktikal-bold font-normal`}
  font-size: 18px;
  line-height: 1.21;
  color: #1c1c33;
`;

const DarkButtonWrapper = styled(ButtonWrapper)`
  background: radial-gradient(circle at 2% 10%, rgba(72, 36, 133, 1) 0%, rgba(28, 28, 51, 1) 100%);
`;

/* --- No Credits variant (dark gradient background) --- */

const NoCreditsCardContainer = styled(BaseCardContainer)`
  background: radial-gradient(circle at 0% 100%, rgba(72, 36, 133, 1) 0%, rgba(0, 0, 26, 1) 50%);
  border: 1px solid rgba(0, 0, 0, 1);
`;

const RestworldBadge = styled(BaseBadge)`
  background: #f0ebf8;
  color: #482485;
`;

const NoCreditsText = styled.span`
  ${tw`w-full font-praktikal-bold font-normal`}
  font-size: 20px;
  line-height: 1.275;
  color: #fafafa;
`;

const GlowButtonWrapper = styled(ButtonWrapper)`
  background: radial-gradient(circle at 0% 1%, rgba(72, 36, 133, 1) 0%, rgba(240, 235, 248, 0.1) 100%);
  box-shadow: 0px 0px 32px 0px rgba(102, 51, 187, 0.75);
`;

const FrostedCardButton = styled(CardButton)`
  background: rgba(250, 250, 250, 0.1);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(250, 250, 250, 0.2);
  }
`;

const PaddedButtonLabel = styled(ButtonLabel)`
  padding: 0 8px;
`;

/* --- Skeleton loading --- */

const shimmer = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #e8e0f0 25%, #f0ebf8 50%, #e8e0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;
  ${shimmer}
`;

const SkeletonContainer = styled(BaseCardContainer)`
  background: #f0ebf8;
  border: 1px solid #dedee1;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 26, 0.05);
  gap: 16px;
`;

const SkeletonBadge = styled(SkeletonBlock)`
  width: 100px;
  height: 24px;
`;

const SkeletonLine = styled(SkeletonBlock)<{ width: string }>`
  width: ${({ width }) => width};
  height: 20px;
`;

const SkeletonButton = styled(SkeletonBlock)`
  ${tw`w-full`}
  height: 48px;
  border-radius: 8px;
`;
